// Which menu link is the page the reader is on, for both templates'
// menus. No imports, so the harness can run it in node.
//
// The same site is served two ways, and a menu link has to light up
// under both: on a custom domain the page is /conditions, and on the
// TrailsIQ address it is /site/{org}/conditions while the link still
// says /conditions (a click handler adds the prefix on the way out).
// Comparing the link to the raw pathname matched only the first, so
// the preview and every site without its own domain never marked a
// page.

function cleanPath(path) {
    const p = String(path ?? '').replace(/[?#].*$/, '');
    return p.length > 1 ? p.replace(/\/+$/, '') : (p || '/');
}

// The page's own path with the site's prefix taken off: /site/{org}
// on the TrailsIQ preview, or whatever `prefix` names (/{org} on the
// public host, where the path alone cannot say it is a prefix).
export function sitePath(pathname, prefix = '') {
    const p = cleanPath(pathname);
    if (prefix && (p === prefix || p.startsWith(`${prefix}/`))) {
        return p === prefix ? '/' : cleanPath(p.slice(prefix.length));
    }
    const m = p.match(/^\/site\/[^/]+(\/.*)?$/);
    return m ? (m[1] ? cleanPath(m[1]) : '/') : p;
}

// The path a link points at on this site, or null when it is not a
// page here: blank, a bare #anchor, another website, or a jump to a
// section of the home page ("/#news" is the home page, not a page).
function linkPath(href, location, prefix) {
    const h = String(href ?? '').trim();
    if (!h || h.startsWith('#') || /^(mailto|tel):/i.test(h)) return null;
    let path = h;
    if (/^https?:\/\//i.test(h)) {
        let url;
        try { url = new URL(h); } catch { return null; }
        if (!location?.host || url.host !== location.host) return null;
        path = url.pathname + url.hash;
    }
    if (!path.startsWith('/')) return null;
    const clean = sitePath(path, prefix);
    if (clean === '/' && path.includes('#')) return null;
    return clean;
}

// Index of the link for the current page, or -1. A section's own
// pages count as the section (/news/a-post lights News), and the most
// specific link wins so a menu holding both /news and /news/archive
// lights one of them, not both.
export function activeLinkIndex(links, location, prefix = '') {
    if (!location?.pathname) return -1;
    const here = sitePath(location.pathname, prefix);
    let best = -1;
    let bestLen = -1;
    (links || []).forEach((link, i) => {
        const p = linkPath(link?.href, location, prefix);
        if (p === null) return;
        const hit = p === '/' ? here === '/' : (here === p || here.startsWith(`${p}/`));
        if (hit && p.length > bestLen) { best = i; bestLen = p.length; }
    });
    return best;
}
